import { useRef, useMemo } from 'react';
import { MORSE_MAP } from '../constants';

export const useAudioEngine = () => {
    const audioCtxRef = useRef(null);
    const oscRef = useRef(null);
    const gainRef = useRef(null);

    // Noise nodes
    const noiseNodeRef = useRef(null);
    const noiseGainRef = useRef(null);
    const noiseFilterRef = useRef(null);

    // QSB (Fading) nodes
    const signalBusRef = useRef(null); // All tones go here
    const qsbGainRef = useRef(null);   // Applies fading
    const qsbLfoRef = useRef(null);    // Modulates fading
    const qsbLfoGainRef = useRef(null); // Controls fade depth

    const isPlayingRef = useRef(false);
    const activeOscillators = useRef(new Set());
    const activePlaybacks = useRef(0);

    // Error buzzer volume (0 = muted, 1 = full volume)
    const errorVolumeRef = useRef(1.0);

    const trackOscillator = (osc) => {
        activeOscillators.current.add(osc);
        osc.onended = () => activeOscillators.current.delete(osc);
    };

    const initAudio = async () => {
        if (!audioCtxRef.current) {
            audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
        }
        const ctx = audioCtxRef.current;

        // Initialize Signal Bus & QSB Chain if not exists
        if (!signalBusRef.current) {
            const signalBus = ctx.createGain();
            signalBus.gain.value = 1.0;

            const qsbGain = ctx.createGain();
            qsbGain.gain.value = 1.0;

            const qsbLfo = ctx.createOscillator();
            qsbLfo.type = 'sine';
            qsbLfo.frequency.value = 0.2;
            qsbLfo.start();

            const qsbLfoGain = ctx.createGain();
            qsbLfoGain.gain.value = 0;

            qsbLfo.connect(qsbLfoGain);
            qsbLfoGain.connect(qsbGain.gain);
            signalBus.connect(qsbGain);
            qsbGain.connect(ctx.destination);

            signalBusRef.current = signalBus;
            qsbGainRef.current = qsbGain;
            qsbLfoRef.current = qsbLfo;
            qsbLfoGainRef.current = qsbLfoGain;
        }

        if (ctx.state === 'suspended') {
            await ctx.resume();
        }
    };

    const setNoise = async (level) => {
        await initAudio();
        const ctx = audioCtxRef.current;
        if (noiseNodeRef.current) {
            try { noiseNodeRef.current.stop(); } catch (e) { }
            noiseNodeRef.current = null;
        }
        if (level <= 0) return;
        if (ctx.state !== 'running') return;

        const bufferSize = ctx.sampleRate * 2;
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const data = buffer.getChannelData(0);
        for (let i = 0; i < bufferSize; i++) {
            data[i] = Math.random() * 2 - 1;
        }

        const noise = ctx.createBufferSource();
        noise.buffer = buffer;
        noise.loop = true;

        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 1000;
        filter.Q.value = 1;

        const gain = ctx.createGain();
        gain.gain.value = (level / 10) * 0.4;

        noise.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);

        noise.start();
        noiseNodeRef.current = noise;
        noiseFilterRef.current = filter;
        noiseGainRef.current = gain;
    };

    const stopNoise = () => {
        if (noiseNodeRef.current) {
            try { noiseNodeRef.current.stop(); } catch (e) { }
            noiseNodeRef.current = null;
        }
    };

    const setQsb = async (level) => {
        await initAudio();
        if (qsbLfoGainRef.current && qsbGainRef.current && qsbLfoRef.current) {
            const ctx = audioCtxRef.current;
            if (ctx.state !== 'running') return;
            const now = ctx.currentTime;
            const depth = (level / 10) * 0.6;
            const speed = 0.1 + (level * 0.02);
            qsbLfoRef.current.frequency.setValueAtTime(speed, now);
            qsbLfoGainRef.current.gain.linearRampToValueAtTime(depth, now + 0.5);
        }
    };

    const noiseBufferRef = useRef(null);
    const brownNoiseBufferRef = useRef(null);

    const getNoiseBuffer = (ctx) => {
        if (!noiseBufferRef.current) {
            const bufferSize = ctx.sampleRate * 2;
            const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const data = buffer.getChannelData(0);
            for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
            noiseBufferRef.current = buffer;
        }
        return noiseBufferRef.current;
    };

    const getBrownNoiseBuffer = (ctx) => {
        if (!brownNoiseBufferRef.current) {
            const bufferSize = ctx.sampleRate * 2;
            const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const data = buffer.getChannelData(0);
            let lastOut = 0;
            for (let i = 0; i < bufferSize; i++) {
                const white = Math.random() * 2 - 1;
                data[i] = (lastOut + (0.02 * white)) / 1.02;
                lastOut = data[i];
                data[i] *= 3.5;
            }
            brownNoiseBufferRef.current = buffer;
        }
        return brownNoiseBufferRef.current;
    };

    const AUDIO_PROFILES = {
        'sounder_1844': { type: 'click', freqUp: 1200, freqDown: 800, filterQ: 0.5, duration: 0.05 },
        'sounder_standard': { type: 'click', freqUp: 2200, freqDown: 1800, filterQ: 2.0, duration: 0.03 },
        'spark': { type: 'spark', baseFreq: 600, noiseMix: 0.8, detune: 5 },
        'cw': { type: 'sine', attack: 0.005, release: 0.005 },
        'cw_tubes': { type: 'sine', attack: 0.02, release: 0.02, saturation: 0.3 }
    };

    const playClick = (time, centerFreq, Q, volumeScale = 1.0) => {
        const ctx = audioCtxRef.current;
        if (!ctx) return;
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.setValueAtTime(centerFreq, time);
        filter.Q.value = Q;

        const noiseSource = ctx.createBufferSource();
        noiseSource.buffer = getNoiseBuffer(ctx);

        noiseSource.connect(filter);
        filter.connect(gain);
        gain.connect(ctx.destination);

        gain.gain.setValueAtTime(0, time);
        gain.gain.linearRampToValueAtTime(0.8 * volumeScale, time + 0.001);
        gain.gain.exponentialRampToValueAtTime(0.01, time + 0.03 + (1 / Q) * 0.01);

        noiseSource.start(time);
        noiseSource.stop(time + 0.1);
    };

    const playTone = (duration, freq = 700, type = 'sine') => {
        initAudio();
        const ctx = audioCtxRef.current;
        const now = ctx.currentTime;

        if (type === 'sounder') type = 'sounder_standard';

        if (type.startsWith('sounder') || AUDIO_PROFILES[type]?.type === 'click') {
            const profile = AUDIO_PROFILES[type] || AUDIO_PROFILES['sounder_standard'];
            playClick(now, profile.freqDown, profile.filterQ, 1.0);
            playClick(now + duration, profile.freqUp, profile.filterQ, 0.8);
            return;
        }

        const gain = ctx.createGain();
        let osc;
        gain.connect(signalBusRef.current || ctx.destination);

        if (type === 'spark') {
            const osc1 = ctx.createOscillator();
            osc1.type = 'sawtooth';
            osc1.frequency.setValueAtTime(freq, now);
            const osc2 = ctx.createOscillator();
            osc2.type = 'sawtooth';
            osc2.frequency.setValueAtTime(freq - 4, now);

            const noise = ctx.createBufferSource();
            noise.buffer = getNoiseBuffer(ctx);
            noise.loop = true;
            const noiseFilter = ctx.createBiquadFilter();
            noiseFilter.type = 'bandpass';
            noiseFilter.frequency.value = 2500;
            noiseFilter.Q.value = 0.5;
            const noiseGain = ctx.createGain();
            noiseGain.gain.value = 0.8;

            const mixGain = ctx.createGain();
            mixGain.gain.value = 0.15;

            osc1.connect(mixGain);
            osc2.connect(mixGain);
            mixGain.connect(gain);
            noise.connect(noiseFilter);
            noiseFilter.connect(noiseGain);
            noiseGain.connect(gain);

            osc1.start(now);
            osc1.stop(now + duration + 0.05);
            osc2.start(now);
            osc2.stop(now + duration + 0.05);
            noise.start(now);
            noise.stop(now + duration + 0.05);
        } else {
            osc = ctx.createOscillator();
            osc.type = (type === 'cw_tubes' || type === 'sounder') ? 'sine' : type;
            osc.frequency.setValueAtTime(freq, now);
            osc.connect(gain);
        }

        let attack = 0.005;
        let release = 0.005;
        if (type === 'cw_tubes') { attack = 0.015; release = 0.015; }
        if (duration < attack + release) { attack = duration / 2; release = duration / 2; }

        gain.gain.cancelScheduledValues(now);
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.1, now + attack);
        if (duration > attack + release) gain.gain.setValueAtTime(0.1, now + duration - release);
        gain.gain.linearRampToValueAtTime(0, now + duration);

        if (osc) {
            osc.start(now);
            osc.stop(now + duration + 0.05);
            trackOscillator(osc);
        }
    };

    const startTone = (freq = 700, type = 'sine') => {
        initAudio();
        if (oscRef.current) return;
        const ctx = audioCtxRef.current;
        const now = ctx.currentTime;

        if (type === 'sounder' || type === 'click') {
            const profile = AUDIO_PROFILES['sounder_standard'];
            playClick(now, profile.freqDown, profile.filterQ, 1.0);
            oscRef.current = { type: 'sounder' };
            return;
        }

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(freq, now);
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(0.1, now + 0.005);
        osc.connect(gain);
        gain.connect(signalBusRef.current || ctx.destination);
        osc.start(now);
        oscRef.current = osc;
        gainRef.current = gain;
        trackOscillator(osc);
    };

    const stopTone = () => {
        if (oscRef.current) {
            const ctx = audioCtxRef.current;
            const now = ctx.currentTime;
            if (oscRef.current.type === 'sounder') {
                const profile = AUDIO_PROFILES['sounder_standard'];
                playClick(now, profile.freqUp, profile.filterQ, 0.8);
                oscRef.current = null;
                return;
            }
            if (!gainRef.current) return;
            const gain = gainRef.current.gain;
            gain.cancelScheduledValues(now);
            gain.setValueAtTime(gain.value, now);
            gain.linearRampToValueAtTime(0, now + 0.005);
            oscRef.current.stop(now + 0.05);
            oscRef.current = null;
            gainRef.current = null;
        }
    };

    const playError = () => {
        const volume = errorVolumeRef.current;
        if (volume <= 0) return; // Muted

        initAudio();
        const ctx = audioCtxRef.current;
        const now = ctx.currentTime;
        const duration = 0.3;
        const freq = 150;

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sawtooth';
        osc.frequency.setValueAtTime(freq, now);
        osc.connect(gain);
        gain.connect(signalBusRef.current || ctx.destination);

        const baseVolume = 0.1;
        const scaledVolume = baseVolume * volume;
        gain.gain.setValueAtTime(0, now);
        gain.gain.linearRampToValueAtTime(scaledVolume, now + 0.005);
        gain.gain.setValueAtTime(scaledVolume, now + duration - 0.005);
        gain.gain.linearRampToValueAtTime(0, now + duration);

        osc.start(now);
        osc.stop(now + duration + 0.05);
        trackOscillator(osc);
    };

    const setErrorVolume = (volume) => {
        errorVolumeRef.current = Math.max(0, Math.min(1, volume));
    };

    const playCorrect = () => playTone(0.1, 1000, 'sine');
    const playSuccess = playCorrect;

    const playLevelUp = () => {
        playTone(0.1, 800, 'sine');
        setTimeout(() => playTone(0.1, 1000, 'sine'), 150);
        setTimeout(() => playTone(0.2, 1200, 'sine'), 300);
    };

    const playbackIdRef = useRef(0);
    const stopPlayback = () => {
        activePlaybacks.current = 0;
        isPlayingRef.current = false;
        playbackIdRef.current++;
        activeOscillators.current.forEach(osc => { try { osc.stop(); } catch (e) { } });
        activeOscillators.current.clear();
        stopTone();
    };

    const playSequence = async (morseSequence, charWpm = 20, spacingWpm = null, onCharStart = null, freq = 700, onKey = null) => {
        const myId = playbackIdRef.current;
        await initAudio();
        activePlaybacks.current++;
        isPlayingRef.current = true;

        // Small delay to ensure audio graph is ready (prevents first character dropout)
        await new Promise(r => setTimeout(r, 20));

        const charUnit = 1.2 / charWpm;
        const charUnitMs = charUnit * 1000;

        for (let char of morseSequence) {
            if (!isPlayingRef.current || playbackIdRef.current !== myId) break;

            if (char === '.') {
                if (onKey) onKey(true);
                playTone(charUnit, freq);
                await new Promise(r => setTimeout(r, charUnitMs));
                if (onKey) onKey(false);
            } else if (char === '-') {
                if (onKey) onKey(true);
                playTone(charUnit * 3, freq);
                await new Promise(r => setTimeout(r, charUnitMs * 3));
                if (onKey) onKey(false);
            }
            await new Promise(r => setTimeout(r, charUnitMs));
        }

        if (myId === playbackIdRef.current) {
            activePlaybacks.current--;
            if (activePlaybacks.current <= 0) isPlayingRef.current = false;
        }
    };

    const playText = async (text, charWpm = 20, spacingWpm = null, onCharStart = null, freq = 700, toneType = 'sine', onKey = null) => {
        const myId = playbackIdRef.current;
        await initAudio();
        activePlaybacks.current++;
        isPlayingRef.current = true;
        await new Promise(r => setTimeout(r, 20));

        const effectiveSpacingWpm = (spacingWpm && spacingWpm < charWpm) ? spacingWpm : charWpm;
        const charUnit = 1.2 / charWpm;
        const charUnitMs = charUnit * 1000;
        const spacingUnitMs = (1.2 / effectiveSpacingWpm) * 1000;

        const chars = text.toUpperCase().split('');
        for (let i = 0; i < chars.length; i++) {
            if (!isPlayingRef.current || playbackIdRef.current !== myId) break;
            const char = chars[i];
            if (char === ' ') {
                if (onCharStart) onCharStart(i);
                await new Promise(r => setTimeout(r, spacingUnitMs * 7));
            } else if (char === '\n') {
                await new Promise(r => setTimeout(r, spacingUnitMs * 14));
            } else if (char === '|') {
                await new Promise(r => setTimeout(r, spacingUnitMs * 28));
            } else {
                const code = MORSE_MAP[char];
                if (code) {
                    if (onCharStart) onCharStart(i);
                    for (let symbol of code) {
                        if (!isPlayingRef.current || playbackIdRef.current !== myId) break;
                        const symDuration = symbol === '.' ? charUnit : charUnit * 3;
                        if (onKey) onKey(true);
                        playTone(symDuration, freq, toneType);
                        await new Promise(r => setTimeout(r, symDuration * 1000));
                        if (onKey) onKey(false);
                        await new Promise(r => setTimeout(r, charUnitMs));
                    }
                }
                await new Promise(r => setTimeout(r, spacingUnitMs * 3));
            }
        }
        if (myId === playbackIdRef.current) {
            activePlaybacks.current--;
            if (activePlaybacks.current <= 0) isPlayingRef.current = false;
        }
    };

    const playPileup = async (texts, wpm, farnsworth) => {
        initAudio();
        const myId = playbackIdRef.current;
        isPlayingRef.current = true;
        const promises = texts.map(text => {
            const freq = 500 + Math.random() * 400;
            const delay = Math.random() * 1000;
            return new Promise(resolve => {
                setTimeout(() => {
                    if (playbackIdRef.current !== myId) return resolve();
                    playText(text, wpm, farnsworth, null, freq).then(resolve);
                }, delay);
            });
        });
        await Promise.all(promises);
    };

    const ambienceNodesRef = useRef([]);
    const stopAmbience = (fadeOutDuration = 2.0) => {
        const oldNodes = [...ambienceNodesRef.current];
        ambienceNodesRef.current = [];
        if (oldNodes.length > 0) {
            const masterGain = oldNodes[0];
            if (masterGain?.gain && audioCtxRef.current) {
                try {
                    const now = audioCtxRef.current.currentTime;
                    masterGain.gain.cancelScheduledValues(now);
                    masterGain.gain.setValueAtTime(masterGain.gain.value, now);
                    masterGain.gain.linearRampToValueAtTime(0, now + fadeOutDuration);
                } catch (e) { }
            }
            setTimeout(() => {
                oldNodes.forEach(node => { try { node.stop(); } catch (e) { } try { node.disconnect(); } catch (e) { } });
            }, fadeOutDuration * 1000 + 100);
        }
    };

    const setAmbience = async (type, volume = 0.5) => {
        await initAudio();
        const ctx = audioCtxRef.current;
        stopAmbience(2.0);
        if (!type || type === 'none') return;
        const nodes = [];
        const masterGain = ctx.createGain();
        masterGain.gain.value = 0;
        masterGain.connect(ctx.destination);
        const now = ctx.currentTime;
        masterGain.gain.linearRampToValueAtTime(volume, now + 2.0);
        nodes.push(masterGain);

        const createNoise = (noiseType = 'white') => {
            const bufferSize = ctx.sampleRate * 2;
            const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
            const data = buffer.getChannelData(0);
            let lastOut = 0;
            for (let i = 0; i < bufferSize; i++) {
                const white = Math.random() * 2 - 1;
                if (noiseType === 'brown') {
                    data[i] = (lastOut + (0.02 * white)) / 1.02;
                    lastOut = data[i];
                    data[i] *= 3.5;
                } else if (noiseType === 'pink') {
                    const b0 = 0.99886 * (lastOut || 0) + white * 0.0555179;
                    const b1 = 0.99332 * (b0 || 0) + white * 0.0750759;
                    const b2 = 0.96900 * (b1 || 0) + white * 0.1538520;
                    data[i] = (b0 + b1 + b2 + white * 0.5362) * 0.11;
                    lastOut = b0;
                } else data[i] = white;
            }
            const src = ctx.createBufferSource();
            src.buffer = buffer; src.loop = true; return src;
        };

        if (type === 'ocean' || type === 'maritime') {
            const noise = createNoise('brown');
            const filter = ctx.createBiquadFilter();
            filter.type = 'lowpass'; filter.frequency.value = 400;
            const lfo1 = ctx.createOscillator(); lfo1.frequency.value = 0.07;
            const lfo1Gain = ctx.createGain(); lfo1Gain.gain.value = 250;
            lfo1.connect(lfo1Gain); lfo1Gain.connect(filter.frequency);
            noise.connect(filter); filter.connect(masterGain);
            noise.start(); lfo1.start();
            nodes.push(noise, filter, lfo1, lfo1Gain);
        } else if (type === 'storm') {
            const noise = createNoise('brown');
            const filter = ctx.createBiquadFilter();
            filter.type = 'lowpass'; filter.frequency.value = 350;
            noise.connect(filter); filter.connect(masterGain);
            const rain = createNoise('pink');
            const rainFilter = ctx.createBiquadFilter();
            rainFilter.type = 'highpass'; rainFilter.frequency.value = 1200;
            rain.connect(rainFilter); rainFilter.connect(masterGain);
            noise.start(); rain.start();
            nodes.push(noise, filter, rain, rainFilter);
        }
        ambienceNodesRef.current = nodes;
    };

    const playSoundEffect = async (effectType) => {
        await initAudio();
        const ctx = audioCtxRef.current;
        if (!ctx) return;
        const now = ctx.currentTime;
        const masterGain = signalBusRef.current || ctx.destination;

        if (effectType === 'clack') {
            const tap = (delay, vol) => {
                // 1. Heavy Thud
                const g = ctx.createGain();
                const f = ctx.createBiquadFilter();
                f.type = 'lowpass'; f.frequency.value = 800;
                const n = ctx.createBufferSource();
                n.buffer = getBrownNoiseBuffer(ctx);
                n.connect(f); f.connect(g); g.connect(masterGain);
                g.gain.setValueAtTime(0, now + delay);
                g.gain.linearRampToValueAtTime(vol, now + delay + 0.002);
                g.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.05);
                n.start(now + delay); n.stop(now + delay + 0.1);

                // 2. Metallic Ping
                const g2 = ctx.createGain();
                const f2 = ctx.createBiquadFilter();
                f2.type = 'bandpass'; f2.frequency.value = 2500;
                f2.Q.value = 5.0;
                const n2 = ctx.createBufferSource();
                n2.buffer = getNoiseBuffer(ctx);
                n2.connect(f2); f2.connect(g2); g2.connect(masterGain);
                g2.gain.setValueAtTime(0, now + delay);
                g2.gain.linearRampToValueAtTime(vol * 0.3, now + delay + 0.001);
                g2.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.02);
                n2.start(now + delay); n2.stop(now + delay + 0.05);
            };
            tap(0, 0.5); tap(0.12, 0.25);
        } else if (effectType === 'click') {
            const g = ctx.createGain();
            const f = ctx.createBiquadFilter();
            f.type = 'bandpass'; f.frequency.value = 2500;
            const n = ctx.createBufferSource();
            n.buffer = getNoiseBuffer(ctx);
            n.connect(f); f.connect(g); g.connect(masterGain);
            g.gain.setValueAtTime(0, now);
            g.gain.linearRampToValueAtTime(0.5, now + 0.001);
            g.gain.exponentialRampToValueAtTime(0.001, now + 0.02);
            n.start(now); n.stop(now + 0.05);
        } else if (effectType === 'error') {
            const tap = (delay, freq, vol) => {
                const g = ctx.createGain();
                const f = ctx.createBiquadFilter();
                f.type = 'lowpass'; f.frequency.value = freq;
                const n = ctx.createBufferSource();
                n.buffer = getBrownNoiseBuffer(ctx);
                n.connect(f); f.connect(g); g.connect(masterGain);
                g.gain.setValueAtTime(0, now + delay);
                g.gain.linearRampToValueAtTime(vol, now + delay + 0.005);
                g.gain.exponentialRampToValueAtTime(0.001, now + delay + 0.1);
                n.start(now + delay); n.stop(now + delay + 0.2);
            };
            tap(0, 150, 0.6); tap(0.05, 100, 0.4);
        } else if (effectType === 'whistle') {
            const freqs = [400, 500, 600, 800, 1000]; // Rich whistle chord
            const masterWhistleGain = ctx.createGain();
            masterWhistleGain.connect(masterGain);

            masterWhistleGain.gain.setValueAtTime(0, now);
            masterWhistleGain.gain.linearRampToValueAtTime(0.5, now + 0.1);
            masterWhistleGain.gain.setValueAtTime(0.5, now + 1.2);
            masterWhistleGain.gain.exponentialRampToValueAtTime(0.001, now + 1.8);

            freqs.forEach(f => {
                const osc = ctx.createOscillator();
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(f + (Math.random() * 20 - 10), now);

                const lfo = ctx.createOscillator();
                const lfoGain = ctx.createGain();
                lfo.frequency.value = 5 + Math.random() * 2;
                lfoGain.gain.value = 8;
                lfo.connect(lfoGain);
                lfoGain.connect(osc.frequency);

                osc.connect(masterWhistleGain);
                osc.start(now);
                lfo.start(now);
                osc.stop(now + 1.8);
                lfo.stop(now + 1.8);
            });

            const noise = ctx.createBufferSource();
            noise.buffer = getNoiseBuffer(ctx);
            const noiseFilter = ctx.createBiquadFilter();
            noiseFilter.type = 'highpass';
            noiseFilter.frequency.value = 4000;
            noiseFilter.Q.value = 0.5;
            const noiseGain = ctx.createGain();
            noiseGain.gain.setValueAtTime(0, now);
            noiseGain.gain.linearRampToValueAtTime(0.1, now + 0.1);
            noiseGain.gain.exponentialRampToValueAtTime(0.001, now + 1.5);

            noise.connect(noiseFilter);
            noiseFilter.connect(noiseGain);
            noiseGain.connect(masterWhistleGain);
            noise.start(now);
            noise.stop(now + 1.8);
        }
    };

    const triggerStaticBurst = (duration = 0.5, intensity = 0.5) => {
        if (!audioCtxRef.current) return;
        const ctx = audioCtxRef.current;
        const now = ctx.currentTime;
        const noise = ctx.createBufferSource();
        noise.buffer = getNoiseBuffer(ctx);
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(intensity, now);
        gain.gain.exponentialRampToValueAtTime(0.01, now + duration);
        noise.connect(gain); gain.connect(ctx.destination);
        noise.start(now); noise.stop(now + duration);
    };

    return useMemo(() => ({
        initAudio,
        setNoise,
        stopNoise,
        setQsb,
        setAmbience,
        stopAmbience,
        playSoundEffect,
        triggerStaticBurst,
        playTone,
        startTone,
        stopTone,
        playError,
        setErrorVolume,
        playCorrect,
        playSuccess,
        playLevelUp,
        stopPlayback,
        playSequence,
        playText,
        playPileup
    }), []);
};
